// The picture of a solid drawn on a slant, and of a net: the flat pattern
// of faces that folds into a solid. Plain React SVG, like
// practice-graphs.tsx.
//
// A line is solid or dashed, never a second colour: a dashed line on a
// solid is an edge behind it, and a dashed line on a net is a fold.

import {
  SOLID_LABEL_SIZE,
  describeNet,
  describeNetGrid,
  describeSolid3d,
  netPicture,
  solidPicture,
  type NetGridSpec,
  type NetSpec,
  type Solid3dSpec,
  type SolidBox,
  type SolidLine,
} from './practice-solid-data';

const INK = '#2d4a3e';
const FACE_FILL = '#eef3ee';
const DASHES = '5 4';

const fixed = (value: number) => value.toFixed(2);

function viewBoxOf(box: SolidBox): string {
  return `${fixed(box.x)} ${fixed(box.y)} ${fixed(box.width)} ${fixed(box.height)}`;
}

function Line({
  line,
  kind,
  dashed,
}: {
  line: SolidLine;
  kind: string;
  dashed: boolean;
}) {
  return (
    <line
      data-line={kind}
      x1={fixed(line.from.x)}
      y1={fixed(line.from.y)}
      x2={fixed(line.to.x)}
      y2={fixed(line.to.y)}
      stroke={INK}
      strokeWidth={dashed ? '1.5' : '2.2'}
      strokeLinecap="round"
      strokeDasharray={dashed ? DASHES : undefined}
    />
  );
}

function NetView({ net }: { net: NetSpec | NetGridSpec }) {
  const picture = netPicture(net);

  return (
    <figure className="m-0 max-w-md">
      <svg
        role="img"
        aria-label={
          net.type === 'net_grid' ? describeNetGrid(net) : describeNet(net)
        }
        viewBox={viewBoxOf(picture.box)}
        className="mx-auto block h-auto w-full"
        style={{ maxWidth: `${String(Math.round(picture.box.width * 1.4))}px` }}
      >
        {picture.faces.map((face, index) => (
          <path
            key={index}
            data-face={face.shape}
            d={face.d}
            fill={FACE_FILL}
            // A face with straight sides takes its lines from the cuts and
            // folds; a curved face is its own outline.
            stroke={face.shape === 'polygon' ? 'none' : INK}
            strokeWidth="2.2"
            strokeLinejoin="round"
          />
        ))}
        {picture.folds.map((line, index) => (
          <Line key={index} line={line} kind="fold" dashed />
        ))}
        {picture.cuts.map((line, index) => (
          <Line key={index} line={line} kind="cut" dashed={false} />
        ))}
      </svg>
    </figure>
  );
}

function SolidView({ solid }: { solid: Solid3dSpec }) {
  const picture = solidPicture(solid);

  return (
    <figure className="m-0 max-w-md">
      <svg
        role="img"
        aria-label={describeSolid3d(solid)}
        viewBox={viewBoxOf(picture.box)}
        className="mx-auto block h-auto w-full"
        style={{ maxWidth: `${String(Math.round(picture.box.width * 1.4))}px` }}
      >
        {picture.faces.map((d, index) => (
          <path key={index} data-face="seen" d={d} fill={FACE_FILL} />
        ))}
        {picture.hidden.map((line, index) => (
          <Line key={index} line={line} kind="hidden" dashed />
        ))}
        {picture.edges.map((line, index) => (
          <Line key={index} line={line} kind="edge" dashed={false} />
        ))}
        {picture.curves.map((curve, index) => (
          <path
            key={index}
            data-line={curve.hidden ? 'hidden' : 'edge'}
            d={curve.d}
            fill="none"
            stroke={INK}
            strokeWidth={curve.hidden ? '1.5' : '2.2'}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray={curve.hidden ? DASHES : undefined}
          />
        ))}
        {picture.labels.map((label) => (
          <text
            key={label.edge}
            data-label={label.edge}
            x={fixed(label.x)}
            y={fixed(label.y)}
            dy="0.35em"
            textAnchor="middle"
            fill={INK}
            fontSize={SOLID_LABEL_SIZE}
            fontWeight="700"
          >
            {label.text}
          </text>
        ))}
      </svg>
    </figure>
  );
}

export function SolidPictureView({
  solid,
}: {
  solid: Solid3dSpec | NetSpec | NetGridSpec;
}) {
  return solid.type === 'solid_3d' ? (
    <SolidView solid={solid} />
  ) : (
    <NetView net={solid} />
  );
}
